import { memo, useCallback, useMemo } from "react";
import { classNames } from "helpers";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "ui/components/Button/Button";
import { MarketplaceData, WizardPictureItem } from "../../types";
import VideoSection from "../MarketplaceStep/sections/VideoSection";
import { PictureStepProps } from "./types";

// Pojedinačna kartica unutar "Odabrane slike" sekcije. Cijela kartica je
// drag handle (pointer sensor s 6px threshold-om u parentu prevents akcidentalni
// drag pri kliku za uklanjanje).
const SortablePictureCard: React.FC<{
  picture: WizardPictureItem;
  order: number;
  onRemove: () => void;
}> = ({ picture, order, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: picture.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    touchAction: "none",
  };
  const isCover = order === 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="relative rounded-sm overflow-hidden border-2 border-sky-500 shadow-sm cursor-grab active:cursor-grabbing"
    >
      <div className="aspect-[4/3] w-full bg-slate-100">
        <img src={picture.url} alt={picture.name ?? ""} className="w-full h-full object-cover pointer-events-none" />
      </div>

      {isCover && (
        <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-amber-400 text-white text-xs font-semibold shadow pointer-events-none">
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3">
            <path d="M12 2l2.4 6.9 7.6.6-5.8 5 1.8 7.5L12 18l-6 4 1.8-7.5L2 9.5l7.6-.6L12 2z" />
          </svg>
          Naslovna
        </span>
      )}

      {/* Klik na badge s rednim brojem = uklanjanje iz odabira. onPointerDown
          stopPropagation sprječava dnd-kit da zgrabi gest. */}
      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onRemove}
        className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold shadow bg-sky-600 text-white hover:bg-sky-700 cursor-pointer"
        aria-label="Ukloni iz odabira"
      >
        {order + 1}
      </button>

      {picture.name && (
        <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-gradient-to-t from-black/60 to-transparent text-white text-xs font-medium truncate pointer-events-none">
          {picture.name}
        </div>
      )}
    </div>
  );
};

// Korak za odabir i sortiranje slika oglasa. Odabrane slike su u gornjoj
// sekciji (drag-and-drop reorder via @dnd-kit); ne-odabrane su ispod, klikom
// se dodaju na kraj liste.
const PictureStep: React.FC<PictureStepProps> = (props) => {
  const { formValues, setFormValues, errors } = props;
  const { pictures } = formValues;
  const selectedIds = formValues.selectedPictureUploadIds;

  // PointerSensor s activation threshold-om (6px) — sprječava drag pri običnom
  // kliku na "Ukloni" gumbe.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  // Odabrane slike u trenutnom redoslijedu (kako selectedIds slijedi).
  const selectedPictures = useMemo(() => {
    const map = new Map(pictures.map((p) => [p.id, p]));
    return selectedIds.map((id) => map.get(id)).filter((p): p is WizardPictureItem => !!p);
  }, [pictures, selectedIds]);

  // Slike koje nisu (još) odabrane — ostaju u izvornom redoslijedu iz `pictures`.
  const availablePictures = useMemo(
    () => pictures.filter((p) => !selectedIds.includes(p.id)),
    [pictures, selectedIds]
  );

  const handleSelect = useCallback(
    (uploadId: string) => {
      setFormValues((prev) => ({
        ...prev,
        selectedPictureUploadIds: [...prev.selectedPictureUploadIds, uploadId],
      }));
    },
    [setFormValues]
  );

  const handleRemove = useCallback(
    (uploadId: string) => {
      setFormValues((prev) => ({
        ...prev,
        selectedPictureUploadIds: prev.selectedPictureUploadIds.filter((id) => id !== uploadId),
      }));
    },
    [setFormValues]
  );

  // Dodaje sve preostale slike na kraj odabira, redoslijedom kako stoje na vozilu.
  const handleSelectAll = useCallback(() => {
    setFormValues((prev) => ({
      ...prev,
      selectedPictureUploadIds: prev.pictures.map((p) => p.id),
    }));
  }, [setFormValues]);

  const handleClearAll = useCallback(() => {
    setFormValues((prev) => ({ ...prev, selectedPictureUploadIds: [] }));
  }, [setFormValues]);

  // Video stoji uz fotografije jer je i on vizualni sadržaj oglasa, a ne podatak o vozilu.
  const patchMarketplace = useCallback(
    (patch: Partial<MarketplaceData>) =>
      setFormValues((prev) => ({
        ...prev,
        marketplaceData: { ...prev.marketplaceData, ...patch },
      })),
    [setFormValues]
  );

  const handleYoutube = useCallback(
    (next: string) => patchMarketplace({ youtubeUrl: next }),
    [patchMarketplace]
  );

  const handleVideoCallViewing = useCallback(
    (checked: boolean) =>
      setFormValues((prev) => ({
        ...prev,
        marketplaceData: {
          ...prev.marketplaceData,
          extra: { ...prev.marketplaceData.extra, videoCallViewing: checked },
        },
      })),
    [setFormValues]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      setFormValues((prev) => {
        const oldIndex = prev.selectedPictureUploadIds.indexOf(String(active.id));
        const newIndex = prev.selectedPictureUploadIds.indexOf(String(over.id));
        if (oldIndex === -1 || newIndex === -1) return prev;
        return {
          ...prev,
          selectedPictureUploadIds: arrayMove(prev.selectedPictureUploadIds, oldIndex, newIndex),
        };
      });
    },
    [setFormValues]
  );

  return (
    <div className="w-full max-w-5xl mx-auto px-6 py-8">
      <div className="flex flex-col items-center text-center mb-6">
        <h2 className="text-2xl font-bold text-slate-800">Fotografije</h2>
        <p className="text-sm text-slate-500 mt-2 max-w-md">
          Označite koje slike želite prikazati u oglasu. Povucite za promjenu redoslijeda — prva slika je naslovna.
        </p>
      </div>

      {/* Jedna traka iznad slika: stanje odabira lijevo, akcije desno — kao gumbi
          za upload na ostalim ekranima sa slikama (outlined, small). */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-sm text-slate-600">
            <span className="text-sky-600 font-semibold">{selectedIds.length}</span>
            {" od "}
            <span className="font-semibold">{pictures.length}</span>
            {" odabrano"}
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs text-slate-400">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-4 h-4 text-amber-400">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" />
            </svg>
            Naslovna slika prikazuje se prva
          </span>
        </div>

        <div className="flex items-center gap-2">
          {availablePictures.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              children="Dodaj sve"
              onClick={handleSelectAll}
              startIcon="/media/icons/plus.svg"
            />
          )}
          {selectedIds.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              children="Poništi odabir"
              onClick={handleClearAll}
              startIcon="/media/icons/close.svg"
            />
          )}
        </div>
      </div>

      {errors.selectedPictureUploadIds && (
        <div className="text-xs text-red-500 mb-3">{errors.selectedPictureUploadIds}</div>
      )}

      {/* Odabrane — sortabilne preko @dnd-kit */}
      {selectedPictures.length > 0 && (
        <div className="mb-6">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
            Odabrane slike
          </div>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={selectedIds} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {selectedPictures.map((p, idx) => (
                  <SortablePictureCard
                    key={p.id}
                    picture={p}
                    order={idx}
                    onRemove={() => handleRemove(p.id)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}

      {/* Dostupne — klik dodaje u odabir */}
      {availablePictures.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
            Dostupne slike
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {availablePictures.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => handleSelect(p.id)}
                className={classNames(
                  "relative rounded-sm overflow-hidden border-2 border-slate-200 hover:border-sky-300 text-left transition-all bg-slate-100"
                )}
              >
                <div className="aspect-[4/3] w-full">
                  <img src={p.url} alt={p.name ?? ""} className="w-full h-full object-cover" />
                </div>
                <span className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold shadow bg-slate-700/70 text-white">
                  +
                </span>
                {p.name && (
                  <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-gradient-to-t from-black/60 to-transparent text-white text-xs font-medium truncate">
                    {p.name}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {pictures.length === 0 && (
        <div className="border border-dashed border-slate-300 rounded-sm p-8 text-center text-sm text-slate-500">
          Vozilo nema dodanih slika.
        </div>
      )}

      <div className="mt-6">
        <VideoSection
          youtubeUrl={formValues.marketplaceData.youtubeUrl}
          videoCallViewing={formValues.marketplaceData.extra.videoCallViewing}
          onYoutubeChange={handleYoutube}
          onVideoCallViewingChange={handleVideoCallViewing}
        />
      </div>
    </div>
  );
};

export default memo(PictureStep);
